import { word } from "@modules/core/utils/i18n"
import { HUText } from "@modules/ui/components/huText"
import Line from "@modules/ui/components/line"
import { Skeleton } from "primereact/skeleton"
import { useTheme } from "styled-components"

type PayoutDetailsInfoSkeletonProps = {} & React.HTMLAttributes<HTMLDivElement>

export const PayoutDetailsInfoSkeleton: React.FC<PayoutDetailsInfoSkeletonProps> = ({ ...props }) => {
  const theme = useTheme()
  const summaryHeaders = [
    { label: word("finances.payoutDetails.summaryHeaders.quantity"), col: "col-1" },
    { label: word("finances.payoutDetails.summaryHeaders.gross"), col: "col-3" },
    { label: word("finances.payoutDetails.summaryHeaders.fees"), col: "col-2" },
    { label: word("finances.payoutDetails.summaryHeaders.total"), col: "col-3" },
  ]

  const salesHeaders = [
    { label: word("finances.payoutDetails.salesHeaders.type"), col: "col-3" },
    { label: word("finances.payoutDetails.salesHeaders.gross"), col: "col-2" },
    { label: word("finances.payoutDetails.salesHeaders.net"), col: "col-2" },
    { label: word("finances.payoutDetails.salesHeaders.name"), col: "col-3" },
    { label: word("finances.payoutDetails.salesHeaders.date"), col: "col-2" },
  ]

  const payoutTransactionSummary = {
    payment: { label: word("finances.payoutDetails.transactionSummary.payment") },
    refund: { label: word("finances.payoutDetails.transactionSummary.refund") },
    adjustment: {
      label: word("finances.payoutDetails.transactionSummary.adjustement"),
    },
  }

  return (
    <>
      <div className="relative" style={{ marginTop: "-85px", marginLeft: "-20px", marginRight: "-20px" }} {...props}>
        {/* HEADER */}
        <div className="relative flex justify-content-between px-5 pt-8 pb-5 w-full  bg-neutralColor-50 border-round-bottom-3xl">
          {/* Column paid */}
          <div className="flex flex-column justify-content-between">
            <div className="flex flex-column gap-1">
              <Skeleton width="9em" height="1.5em" borderRadius="0.8rem" />
              <Skeleton width="4em" height="2em" borderRadius="0.8rem" className="mt-1" />
            </div>
            <div className="flex flex-column gap-1 mt-4">
              <HUText fontStyle="LS" color={theme.neutralColor600}>
                {word("finances.payoutDetails.accountInformation")}
              </HUText>
              <div className="flex">
                <Skeleton width="15em" height="1.6em" borderRadius="0.8rem" className="mt-1" />
              </div>
            </div>
          </div>
          {/* Column timeline */}
          <div className="flex flex-column">
            <HUText fontStyle="BS" color={theme.neutralColor600} className="mb-3">
              {word("finances.payoutDetails.timeline")}
            </HUText>

            <div className="flex flex-column gap-2">
              <div className="flex gap-2">
                <div
                  className="flex"
                  style={{ width: "16px", height: "16px", borderRadius: "5.6px", background: theme.primaryColor400 }}
                ></div>
                <div className="flex flex-column gap-1">
                  <HUText fontStyle="LM" color={theme.neutralColor600}>
                    {word("finances.payoutDetails.transferMade")}
                  </HUText>
                  <Skeleton width="10.5em" height="0.8em" borderRadius="0.8rem" className="mt-1" />
                </div>
              </div>

              <div className="flex gap-2">
                <div
                  className="flex"
                  style={{ width: "16px", height: "16px", borderRadius: "5.6px", background: theme.neutralColor600 }}
                ></div>
                <div className="flex flex-column gap-1">
                  <HUText fontStyle="LS" color={theme.neutralColor600}>
                    {word("finances.payoutDetails.transferInTransit")}
                  </HUText>
                  <Skeleton width="10.5em" height="0.8em" borderRadius="0.8rem" className="mt-1" />
                </div>
              </div>

              <div className="flex gap-2">
                <div
                  className="flex"
                  style={{ width: "16px", height: "16px", borderRadius: "5.6px", background: theme.neutralColor600 }}
                ></div>
                <div className="flex flex-column gap-1">
                  <HUText fontStyle="LS" color={theme.neutralColor600}>
                    {word("finances.payoutDetails.automatedTransfer")}
                  </HUText>
                  <Skeleton width="10.5em" height="0.8em" borderRadius="0.8rem" className="mt-1" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-column w-full">
        {/* SUMMARY */}
        <div className="flex w-full flex-column mt-6">
          <HUText fontStyle="LM" color={theme.neutralColor600}>
            {word("finances.payoutDetails.headerTab.summary").toUpperCase()}
          </HUText>
          <div className="grid">
            <div className="col"></div>
            {summaryHeaders.map((header) => (
              <div className={header.col} key={`${header.label}_SUMMARY`}>
                <HUText fontStyle="LM" color={theme.neutralColor600}>
                  {header.label}
                </HUText>
              </div>
            ))}
          </div>
          <Line color={theme.neutralColor300} strokeWidth={1} className="m-0 rounded-full mb-2" />
          {Object.values(payoutTransactionSummary).map((summary) => (
            <div className="flex w-full flex-column pt-2" key={summary.label}>
              <div className="grid align-items-end">
                <div className="col">
                  <HUText fontStyle="LM" color={theme.blackAtheleWhiteOwner}>
                    {summary.label}
                  </HUText>
                </div>
                <div className="col-1">
                  <Skeleton width="2em" height="1.4em" borderRadius="0.8rem" />
                </div>
                <div className="col-3">
                  <Skeleton width="4em" height="1.4em" borderRadius="0.8rem" />
                </div>
                <div className="col-2">
                  <Skeleton width="3em" height="1.4em" borderRadius="0.8rem" />
                </div>
                <div className="col-3">
                  <Skeleton width="4em" height="1.4em" borderRadius="0.8rem" />
                </div>
              </div>
              <Line color={theme.neutralColor300} strokeWidth={1} className="m-0 rounded-full" />
            </div>
          ))}
        </div>
        {/* TRANSFER */}
        <div className="flex justify-content-end align-items-baseline w-full gap-4 mt-3">
          <HUText fontStyle="LM" color={theme.blackAtheleWhiteOwner}>
            {word("finances.payoutDetails.transfer")}
          </HUText>
          <Skeleton width="5em" height="1.2em" borderRadius="0.8rem" />
        </div>
        {/* SALES */}
        <div className="flex w-full flex-column mt-6">
          <div className="flex w-full align-items-center justify-content-between mb-3">
            <HUText fontStyle="LM" color={theme.neutralColor600}>
              {word("finances.payoutDetails.headerTab.sales").toUpperCase()}
            </HUText>
            <Skeleton width="8em" height="2em" borderRadius="0.8rem" />
          </div>
          <div className="grid w-full">
            {salesHeaders.map((header) => (
              <div className={header.col} key={`${header.label}_SALES`}>
                <HUText fontStyle="LM" color={theme.neutralColor600} style={{ wordBreak: "break-all" }}>
                  {header.label}
                </HUText>
              </div>
            ))}
          </div>
          <Line color={theme.neutralColor300} strokeWidth={1} className="m-0 rounded-full" />
          <Skeleton width="100%" height="20em" borderRadius="0.8rem" className="mt-1" />
        </div>
        {/* FOOTER */}
        <div className="flex flex-column w-full gap-2 mt-6 mb-4">
          <div className="flex flex-column gap-1">
            <HUText fontStyle="BS" color={theme.neutralColor600}>
              {word("finances.payoutDetails.internalNote")}
            </HUText>
            <Skeleton width="7em" height="1em" borderRadius="0.8rem" className="mt-1" />
          </div>
          <div className="flex flex-column gap-1">
            <HUText fontStyle="BS" color={theme.neutralColor600}>
              {word("finances.payoutDetails.transferMethod")}
            </HUText>
            <Skeleton width="5em" height="1em" borderRadius="0.8rem" className="mt-1" />
          </div>
          <div className="flex flex-column gap-1">
            <HUText fontStyle="BS" color={theme.neutralColor600}>
              {word("finances.payoutDetails.transferTrackingId")}
            </HUText>
            <Skeleton width="15em" height="1em" borderRadius="0.8rem" className="mt-1" />
          </div>
        </div>
      </div>
    </>
  )
}
